import { isEqual } from 'lodash'
import { observer } from 'mobx-react-lite'
import qs from 'qs'
import { ReactNode, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { WorkspaceService } from 'api/services/WorkspaceService'
import { dismissToastMessages } from 'common/ToastMessage'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useAuth } from 'hooks/UseAuth'
import { useCommandLine } from 'hooks/useCommandLine'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useGuestVisitingDoc } from 'hooks/useGuestVisitingDoc'
import { useGuestVisitingHandbook } from 'hooks/useGuestVisitingHandbook'
import useImportModal from 'hooks/useImportModal'
import useUserPreferences from 'hooks/useUserPreferences'
import { useLongHover } from 'hooks/useLongHover'
import { useShowFloatingOrgSidebar } from 'hooks/useShowFloatingOrgSidebar'
import { useSuperAdmin } from 'hooks/useSuperAdmin'
import {
  BRAND_KIT_PATH,
  DESIGN_PATH,
  DOC_PATH,
  FOLDER_DOC_PATH,
  FOLDER_VIEWS_PATH,
  MEDIA_FILE_PATH,
  QUICK_IMPORT_PATH,
  WORKSPACE_IMPORT_PATH,
} from 'paths'
import { setFloatingSidebar } from 'redux/actions'
import { DocPresence } from 'stores/DocPresence'
import { useRootStore } from 'stores/RootStore'
import { RootState } from 'types'
import { ALMANAC_CORE_WORKSPACE_ID } from 'utils/constants'
import { reloadPage } from 'utils/location'
import { NotFound } from './components/NotFound/NotFound'
import { useCurrentFolderShortcut, useFolderCreateShortcut } from './hooks/useFolderCreateShortcut'
import { useImportCreateShortcut } from './hooks/useImportCreateShortcut'
import { useReviewCreateShortcut } from './hooks/useReviewCreateShortcut'
import { useWorkspaceRedirect } from './hooks/useWorkspaceRedirect'
import { useIsPwa } from 'utils/isPwa'
import { useEnforceWorkspaceCreation } from 'hooks/useEnforceWorkspaceCreation'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { SentryRoute } from 'components/SentryRoute'
import { WriterDashboard } from 'components/WorkspaceDashboard/components/WriterDashboard/WriterDashboard'
import { Dashboard } from 'components/WorkspaceDashboard/components/Dashboard/Dashboard'
import { DesignerDashboard } from './components/DesignerDashboard/DesignerDashboard'
import { MobileUpdateModalsWidgetContainer } from './components/MobileUpdateModalsWidgetContainer/MobileUpdateModalsWidgetContainer'
import ForceReloadListener from 'components/ForceReloadListener/ForceReloadListener'
import { BrandKitDashboard } from 'components/WorkspaceDashboard/components/BrandKitDashboard/BrandKitDashboard'
import { PaywallRedirect } from 'components/WorkspaceDashboard/components/PaywallRedirect/PaywallRedirect'
import { useMarkFileAsViewed } from 'screens/Calendar/useMarkFileAsViewed'

interface Props {
  children: ReactNode
}

const useIsImportRoute = () => {
  const workspaceMatch = useRouteMatch(WORKSPACE_IMPORT_PATH)
  const quickMatch = useRouteMatch(QUICK_IMPORT_PATH)

  return !!(workspaceMatch || quickMatch)
}

const WorkspaceDashboard = observer(({ children }: Props) => {
  const dispatch = useDispatch()
  const auth = useAuth()
  const location = useLocation()
  const history = useHistory()
  const loggedIn = !!auth
  const { exitSuperAdminMode, superAdminMode } = useSuperAdmin()
  const { markFileAsViewed } = useMarkFileAsViewed()
  const importModalRoute = useIsImportRoute()
  const docRouteMatch = useRouteMatch(DOC_PATH)
  const mediaFileRouteMatch = useRouteMatch(MEDIA_FILE_PATH)
  const params = qs.parse(location.search, { ignoreQueryPrefix: true })
  const inCompareMode = !!(params.compare || params.submitChanges)
  const articleMeta = useCurrentArticleMeta()
  const floatingSidebar = useSelector(({ floatingSidebar }: RootState) => floatingSidebar, isEqual)
  const docPresence = useRef<DocPresence | null>(null)
  const mainContainerRef = useRef<HTMLDivElement>(null)
  const skipServiceWorker = useFlipperFlag('skip_service_worker')
  const isPWA = useIsPwa()
  const { notifications } = useRootStore()

  const currentWorkspace = useCurrentWorkspace()

  useEnforceWorkspaceCreation()
  useReviewCreateShortcut()
  useImportCreateShortcut()
  useFolderCreateShortcut()
  useCurrentFolderShortcut()

  const { setShowFloatingOrgSidebar } = useShowFloatingOrgSidebar()

  useLongHover(
    mainContainerRef,
    () => {
      if (floatingSidebar) {
        setShowFloatingOrgSidebar(false)
        dispatch(setFloatingSidebar(false))
      }
    },
    () => null,
    1,
  )

  const { workspaceId, workspace, resolved: initialized } = useWorkspaceRedirect()
  const guestVisitingDoc = useGuestVisitingDoc()
  const guestVisitingHandbook = useGuestVisitingHandbook()
  const { openImportModal } = useImportModal()
  useUserPreferences(workspace?.id)

  const { addContext, removeContext } = useCommandLine()

  const hideDashboard = useMemo(() => {
    if (mediaFileRouteMatch) return true
    if (docRouteMatch && inCompareMode) return true
  }, [docRouteMatch, inCompareMode, mediaFileRouteMatch])

  useEffect(() => {
    if (importModalRoute) {
      openImportModal()
      history.replace({
        pathname: location.pathname.replace('/import', ''),
        search: location.search,
      })
    }
  }, [importModalRoute, dispatch, history, location, openImportModal])

  useEffect(() => {
    if (!isPWA) return

    const refreshPage = function (event: any) {
      event.preventDefault()

      const historyTraversal =
        event.persisted || (typeof window.performance != 'undefined' && window.performance.navigation.type === 2)
      if (historyTraversal) {
        // Reload the page if the back button was pressed
        // and the user doesn't have the latest data
        // they only have what's in the cache
        reloadPage()
      }
    }
    window.addEventListener('pageshow', refreshPage)

    return () => {
      window.removeEventListener('pageshow', refreshPage)
    }
  }, [isPWA])

  useEffect(() => {
    if (!workspace) {
      return
    }

    if (workspace.id === ALMANAC_CORE_WORKSPACE_ID) {
      removeContext('workspace')
      return
    }

    addContext('workspace', workspace)

    return () => removeContext('workspace')
  }, [addContext, removeContext, workspace])

  useEffect(() => {
    if (workspace && workspace.id !== ALMANAC_CORE_WORKSPACE_ID && workspace.is_new) {
      WorkspaceService.markAsSeen(workspace.key)
    }
  }, [workspace])

  useEffect(() => {
    if (!articleMeta?.id) {
      return
    }
    docPresence.current = new DocPresence()
  }, [articleMeta?.id])

  useEffect(() => {
    if (articleMeta?.id) markFileAsViewed(articleMeta.id)
  }, [articleMeta?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    notifications.clear()
  }, [workspace]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dismissToastMessages()
    if (superAdminMode) {
      showToastMessage('Currently viewing as super admin', 'success', {
        variant: 'proceed',
        label: 'Exit Mode',
        type: 'button',
        onPress: exitSuperAdminMode,
      })
    }
  }, [superAdminMode, exitSuperAdminMode])

  useEffect(() => {
    if (params.updated) {
      showToastMessage('You were just updated to the latest version of Blaze!', 'success')
    }
  }, [params.updated])

  if (!workspaceId || !initialized) {
    return null
  }

  if (hideDashboard) {
    return (
      <>
        <MobileUpdateModalsWidgetContainer />
        <ForceReloadListener />
        {children}
      </>
    )
  }

  if (
    initialized &&
    !workspace &&
    loggedIn &&
    !guestVisitingHandbook &&
    !guestVisitingDoc &&
    workspaceId !== ALMANAC_CORE_WORKSPACE_ID &&
    !articleMeta?.made_public
  ) {
    return (
      <div>
        <NotFound />
      </div>
    )
  }

  if (skipServiceWorker) {
    localStorage.removeItem('canInstallServiceWorker')
  } else {
    localStorage.setItem('canInstallServiceWorker', 'yes')
  }

  if (!currentWorkspace) {
    return null
  }

  return (
    <>
      <MobileUpdateModalsWidgetContainer />
      <ForceReloadListener />
      <Switch>
        <SentryRoute path={FOLDER_VIEWS_PATH}>
          <PaywallRedirect>
            <Dashboard workspace={currentWorkspace}>{children}</Dashboard>
          </PaywallRedirect>
        </SentryRoute>
        <SentryRoute path={[DOC_PATH, FOLDER_DOC_PATH]}>
          <WriterDashboard workspace={currentWorkspace}>{children}</WriterDashboard>
        </SentryRoute>
        <SentryRoute path={DESIGN_PATH}>
          <DesignerDashboard workspace={currentWorkspace}>{children}</DesignerDashboard>
        </SentryRoute>
        <SentryRoute path={BRAND_KIT_PATH}>
          <PaywallRedirect>
            <BrandKitDashboard workspace={currentWorkspace}>{children}</BrandKitDashboard>
          </PaywallRedirect>
        </SentryRoute>
        <SentryRoute path="/">
          <PaywallRedirect>
            <Dashboard workspace={currentWorkspace}>{children}</Dashboard>
          </PaywallRedirect>
        </SentryRoute>
      </Switch>
    </>
  )
})

export default WorkspaceDashboard
